// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Cleave from 'cleave.js/react';
import { LoadingContentAvatar, LoadingContentStripe } from '../LoadingContentFigures/LoadingContentFigures';

import baggageXS from '../../assets/img/flight/volumetric/baggage/01.png';
import baggageS from '../../assets/img/flight/volumetric/baggage/02.png';
import baggageM from '../../assets/img/flight/volumetric/baggage/03.png';
import baggageL from '../../assets/img/flight/volumetric/baggage/04.png';
import cameraXS from '../../assets/img/flight/volumetric/camera/01.png';
import cameraS from '../../assets/img/flight/volumetric/camera/02.png';
import cameraM from '../../assets/img/flight/volumetric/camera/03.png';
import cameraL from '../../assets/img/flight/volumetric/camera/04.png';
import makeupXS from '../../assets/img/flight/volumetric/makeup/01.png';
import makeupS from '../../assets/img/flight/volumetric/makeup/02.png';
import makeupM from '../../assets/img/flight/volumetric/makeup/03.png';
import makeupL from '../../assets/img/flight/volumetric/makeup/04.png';
import shoesXS from '../../assets/img/flight/volumetric/shoes/01.png';
import shoesS from '../../assets/img/flight/volumetric/shoes/02.png';
import shoesM from '../../assets/img/flight/volumetric/shoes/03.png';
import shoesL from '../../assets/img/flight/volumetric/shoes/04.png';
import supplementsXS from '../../assets/img/flight/volumetric/supplements/01.png';
import supplementsS from '../../assets/img/flight/volumetric/supplements/02.png';
import supplementsM from '../../assets/img/flight/volumetric/supplements/03.png';
import style from './SingleProduct.scss';
import boxStyle from '../../assets/styles/box.scss';
import formStyle from '../../assets/styles/form.scss';
import loadingStripeStyle from '../LoadingContentFigures/LoadingContentFigures.scss';
import icons from '../../assets/styles/icons.scss';

export type ProductType = 'VIEW' | 'SUCCESS' | 'CANCELED';
export type UserType = 'purchaser' | 'traveler';
export type Item = OrderItem | BagItem | ShoppingCartItem;

import type { LocaleState } from '../../modules/locale/duck';
import type { BagItem, Category, OrderItem, ShoppingCartItem } from '../../entities';
import type  { Node } from 'react';

export type Props = {
  localeState: LocaleState;
  categoriesMap?: { [key: string]: Category };
  index: number;
  isLoading?: boolean;
  item: Item;
  userType: UserType;
  showVolumeTier?: boolean;
  renderContent?: (item: Item) => Node;
  buttonChildren: Node[] | Node;
  children: Node[] | Node;
};

export type Property = {
  intl?: string;
  content: string;
};

export type SingleProductDataProps = {
  categoryId: Property;
  detail?: Property;
  originalPackaging: Property;
  price: Property;
  total: Property;
  url: Property;
  weight?: Property;
};

export type SingleProductButtonProps = {
  type?: ProductType;
  amount: number;
  image?: string;
  imageURI?: string;
  name: string;
  subtitleIntl?: string;
  subtitle?: Node;
};

export type SingleProductViewFileProps = {
  fileUrl: string;
  userType?: UserType;
  titleIntl: string;
};

type State = {
  isOpen: boolean;
};

const messages = defineMessages({
  estimatedKgWeight: {
    id: 'product.weight.kg',
  },
  estimatedGWeight: {
    id: 'product.weight.g',
  },
  NOT_IN_TIME: {
    id: 'product.cancelationMotive.notInTime',
  },
  VENDOR_CANCELLED: {
    id: 'product.cancelationMotive.vendorCanceled',
  },
  PACKAGE_TOO_BIG: {
    id: 'product.cancelationMotive.packageTooBig',
  },
  PACKAGE_TOO_HEAVY: {
    id: 'product.cancelationMotive.packageTooHeavy',
  },
});

const volumeImages = {
  '4': {
    'XS': cameraXS,
    'S': cameraS,
    'M': cameraM,
    'L': cameraL,
  },
  /*'6': {
    'XS': supplementsXS,
    'S': supplementsS,
    'M': supplementsM,
  },*/
  '12': {
    'XS': shoesXS,
    'S': shoesS,
    'M': shoesM,
    'L': shoesL,
  },
  /*'13': {
    'XS': makeupXS,
    'S': makeupS,
    'M': makeupM,
    'L': makeupL,
  },*/
  '9': {
    'XS': baggageXS,
    'S': baggageS,
    'M': baggageM,
    'L': baggageL,
  },
};

export const SingleProductLoading = () => (
  <section className={classNames(boxStyle.box, style.singleProductWrap)}>
    <div className={style.loadingWrap}>
      <LoadingContentAvatar colorClassName={loadingStripeStyle.black200}/>
      <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
    </div>
  </section>
);

export const SingleProductRule = () => <hr className={style.rule}/>;

export const SingleProductData = (props: SingleProductDataProps) => {
  const { categoryId, url, originalPackaging, weight, detail, price, total } = props;
  let rUrl = url.content;
  if (URL && rUrl.includes('amazon')) {
    const u = new URL(rUrl);
    rUrl = `${u.origin}${u.pathname}`;
  }
  return (
    <Fragment>
      <div className={classNames(formStyle.row, formStyle.inputRow, style.row)}>
        <FormattedMessage id={url.intl}>{
          txt => <p className={style.url}>{txt}:&nbsp;<a href={rUrl}
                                                         target="_blank"
                                                         rel="nofollow noopener nofollow">{rUrl}</a></p>
        }</FormattedMessage>
      </div>
      <div className={classNames(formStyle.row, formStyle.dualWrap, formStyle.responsiveMedium, style.row)}>
        <article className={classNames(formStyle.col, style.col)}>
          <FormattedMessage id={categoryId.intl}>{
            txt => <p className={style.categoryId}>{txt}: <span>{categoryId.content}</span></p>
          }</FormattedMessage>
          <FormattedMessage id={originalPackaging.intl}>{
            txt => <p className={style.originalPackaging}>{txt}: <span>{originalPackaging.content}</span></p>
          }</FormattedMessage>
          {!!weight && !!weight.content && !!detail && !!detail.content &&
          <FormattedMessage id={weight.intl}>{
            txt => <p className={style.weight}>{txt}: <span>{weight.content} Kg</span></p>
          }</FormattedMessage>}
        </article>
        <article className={classNames(formStyle.col, style.col)}>
          {!!weight && !!weight.content && !!detail && !detail.content &&
          <FormattedMessage id={weight.intl}>{
            txt => <p className={style.weight}>{txt}: <span>{weight.content} Kg</span></p>
          }</FormattedMessage>}
          {!!detail && !!detail.content && <p className={style.detail}>{detail.content}</p>}
        </article>
      </div>
      <SingleProductRule/>
      <div className={classNames(formStyle.row, formStyle.dualWrap, formStyle.responsiveMedium, style.row)}>
        <article className={classNames(formStyle.col, style.col)}>
          <FormattedMessage id={price.intl}>{
            txt => <p className={style.price}>{txt}:&nbsp;<span>$ {price.content}</span></p>
          }</FormattedMessage>
        </article>
        <article className={classNames(formStyle.col, style.col)}>
          <FormattedMessage id={total.intl}>{
            txt => <p className={style.total}>{txt}:&nbsp;<span>$ {total.content}</span></p>
          }</FormattedMessage>
        </article>
      </div>
    </Fragment>
  );
};

export const SingleProductButtonContent = (props: SingleProductButtonProps) => {
  const { type, amount, image, imageURI, name, subtitleIntl, subtitle } = props;
  return (
    <Fragment>
      <div className={style.imageWrap}>
        {!!type &&
        <div className={classNames(style.type, style[type.toLowerCase()])}>
          {type === 'CANCELED' &&
          <i className={icons.iconCloseMedium}/>}
          {type !== 'CANCELED' && <p>{amount}</p>}
        </div>}
        <img className={style.itemImage}
             src={image || imageURI} alt="Order image"/>
      </div>
      <section className={style.title}>
        <p className={style.name}>{name}</p>
        {!!type && !!subtitleIntl &&
        <FormattedMessage id={subtitleIntl}>{
          txt => <p className={style.status}>{txt}</p>
        }</FormattedMessage>}
        {!!type && !!subtitle && subtitle}
      </section>
    </Fragment>);
};

export const SingleProductViewFile = (props: SingleProductViewFileProps) => {
  const { userType = 'purchaser', fileUrl, titleIntl } = props;
  return (
    <FormattedMessage id={titleIntl}>{
      txt => <a className={classNames(style.viewInfoWrap, {
        [style.purchaser]: userType === 'purchaser',
        [style.traveler]: userType === 'traveler',
      })}
                target="_blank"
                href={fileUrl}>
        <i className={icons.iconInvoiceMedium}/> {txt}</a>
    }</FormattedMessage>
  );
};

export default class SingleProduct extends Component<Props, State> {
  state = {
    isOpen: true, // TODO: just for testing
  };

  getVolumeImage = (categoryId: string, volumeTier: string): string => {
    return volumeImages[categoryId][volumeTier];
  };

  buildVolumeBlock() {
    const { item } = this.props;
    return !!item.volumeTier && [4, 12].includes(item.itemCategoryId) &&
      <div className={style.volume}>
        <FormattedMessage id="singleProduct.volume">{
          txt => <p>{txt}</p>
        }</FormattedMessage>
        <img src={this.getVolumeImage(item.itemCategoryId.toString(), item.volumeTier.code)} alt="Qempo"/>
      </div>;
  }

  handleOpen = () => this.setState((prevState: State) => ({
    isOpen: !prevState.isOpen,
  }));

  render() {
    const { isOpen } = this.state;
    const {
      localeState, categoriesMap, index, isLoading, item, userType, showVolumeTier, renderContent,
      buttonChildren, children,
    } = this.props;
    return (
      <section className={classNames(boxStyle.box, style.singleProductWrap)}>
        <button className={style.buttonWrap}
                onClick={this.handleOpen}>
          {buttonChildren}
          <i className={classNames(style.chevron, isOpen ? icons.iconChevronUpMedium : icons.iconChevronDownMedium)}/>
        </button>
        <div className={classNames(formStyle.formWrap, style.contentWrap,
          { [style.open]: isOpen, [style.disabled]: isLoading })}>
          {children}
          {!!renderContent && renderContent(item)}
        </div>
      </section>
    );
  };
}

SingleProduct.propTypes = {
  localeState: PropTypes.object.isRequired,
  categoriesMap: PropTypes.object,
  index: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  item: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
  showVolumeTier: PropTypes.bool,
  renderContent: PropTypes.func,
};



// WEBPACK FOOTER //
// ./src/elements/SingleProduct/SingleProduct.jsx